import {
  clearOrder,
  clearBasket,
  setChainId,
  setPromocode,
  setPromoText,
  setAttribution,
  useAppSelector,
  useAppDispatch,
} from '@/app/store';
import { toast } from 'sonner';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WebApp } from '@/app/store/api/restApi';
import { useSetPromocode } from './useSetPromocode';
import { PATH } from '@/app/routes/path';
import { router } from '@/app/routes/router';
import { getAccessToken } from '@/shared/lib/auth/tokenStorage';
import { isTelegramWebApp, getLaunchParam, parseLaunchAttribution } from '@/shared/lib';
import { useClientStartAppHandleMutation, useLazyGetPromocodeExistsQuery } from '@/entities';
import { stageEmailCodeEntry } from '@/features/emailCodeVerification/lib';
import { parseTelegramEmailVerificationEntry, removeLaunchParams } from '@/features/profileEmailVerification/lib';

export const useInitialiseApp = (isAuthLoading: boolean) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('errors');

  const { checkAndSetPromocode } = useSetPromocode();
  const { chainId } = useAppSelector((state) => state.initial);
  const [handleStartApp] = useClientStartAppHandleMutation();
  const [checkPromocodeExists] = useLazyGetPromocodeExistsQuery();

  useEffect(() => {
    // defining the channel for the user funnel
    dispatch(setAttribution(parseLaunchAttribution(getLaunchParam())));

    const searchChainId = new URLSearchParams(window.location.search).get('chainId');
    if (searchChainId && searchChainId !== chainId) {
      dispatch(setChainId(searchChainId));
      dispatch(clearBasket());
      dispatch(clearOrder());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const applyPromocode = async (promocode: string) => {
      const promocodeError = await checkAndSetPromocode(promocode);
      if (promocodeError === '') toast.success(t('messages.applySuccess', { ns: 'promocodes' }));
      else toast.error(promocodeError);
    };

    if (isAuthLoading) return;

    const searchParams = new URLSearchParams(window.location.search);
    const startParam = searchParams.get('tgWebAppStartParam') ?? searchParams.get('startapp');

    const emailEntry = parseTelegramEmailVerificationEntry(startParam);
    if (emailEntry) {
      window.history.replaceState(
        window.history.state,
        '',
        removeLaunchParams(`${window.location.pathname}${window.location.search}${window.location.hash}`),
      );
      stageEmailCodeEntry(emailEntry);
      void router.navigate(PATH.profileEmailVerify);
      return;
    }

    if (startParam) {
      handleStartApp({
        clientStartAppRequestDto: {
          startApp: startParam,
          webApp: isTelegramWebApp() ? WebApp.Telegram : WebApp.Web,
        },
      });
    }

    if (startParam?.includes('promo_')) {
      const promocode = startParam.split('_')[1];
      if (promocode !== '') {
        if (getAccessToken()) applyPromocode(promocode);
        else
          checkPromocodeExists()
            .unwrap()
            .then((result) => {
              if (result.data) {
                dispatch(setPromocode(undefined));
                dispatch(setPromoText(promocode));
              }
            });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading]);
};
